import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, Button } from 'antd';
import { privacyPolicyRoute, termsOfUseRoute } from '../../config/navigation/routes';
import './PolicyAgreementModal.scss';

const PolicyAgreementModal = ({ isOpen, onApprove, onDisapprove, position = 'center' }) => {
  const [acceptChecked, setAcceptChecked] = useState(false);
  const [rejectChecked, setRejectChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!acceptChecked && !rejectChecked) return;
    
    setSubmitting(true);
    try {
      if (acceptChecked) {
        await onApprove();
      } else if (rejectChecked) {
        onDisapprove();
      }
    } catch (error) {
      console.error('Error processing policy agreement:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptChange = (e) => {
    const checked = e.target.checked;
    setAcceptChecked(checked);
    // If Accept is checked, uncheck Reject (mutually exclusive)
    if (checked) {
      setRejectChecked(false);
    }
  };

  const handleRejectChange = (e) => {
    const checked = e.target.checked;
    setRejectChecked(checked);
    // If Reject is checked, uncheck Accept (mutually exclusive)
    if (checked) {
      setAcceptChecked(false);
    }
  };

  const handlePrivacyPolicyClick = (e) => {
    e.preventDefault();
    // Use absolute URL to ensure the new tab goes directly to the privacy policy page
    // without any intermediate navigation that could trigger redirects
    const absoluteUrl = `${window.location.origin}${privacyPolicyRoute}`;
    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTermsOfUseClick = (e) => {
    e.preventDefault();
    // Use absolute URL to ensure the new tab goes directly to the terms of use page
    // without any intermediate navigation that could trigger redirects
    const absoluteUrl = `${window.location.origin}${termsOfUseRoute}`;
    window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
  };

  // Style for bottom-left positioning (deprecated - now using center for all cases)
  // Keeping this for backward compatibility but center is now the default
  const modalStyle = position === 'bottom-left' ? {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    top: 'auto',
    margin: 0,
    maxWidth: '500px',
  } : {};
  
  // Force center positioning to ensure modal is always centered
  const forceCentered = position === 'center' || position === undefined;

  // Create absolute URLs for the links to ensure they open in new tabs even if click handler doesn't fire
  const privacyPolicyUrl = `${window.location.origin}${privacyPolicyRoute}`;
  const termsOfUseUrl = `${window.location.origin}${termsOfUseRoute}`;

  return (
    <Modal
      title="ICON Use Policies Agreement"
      open={isOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={position === 'bottom-left' ? 500 : 600}
      centered={forceCentered}
      style={modalStyle}
      wrapClassName={position === 'bottom-left' ? 'policy-modal-bottom-left' : ''}
    >
      <div style={{ marginBottom: '24px' }}>
        <p style={{ marginBottom: '16px', fontSize: '16px' }}>
          <strong>ICON has updated our Terms of Use and Privacy Policy.</strong>
        </p>
        <p style={{ marginBottom: '16px' }}>
          Please review the changes by clicking on the following links{' '}
          <a 
            href={privacyPolicyUrl} 
            onClick={handlePrivacyPolicyClick} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            ICON - Privacy Policy
          </a>{' '}
          and{' '}
          <a 
            href={termsOfUseUrl} 
            onClick={handleTermsOfUseClick} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            Terms of Use
          </a>.
        </p>
        <p style={{ marginBottom: '24px', color: '#666' }}>
          <strong>Note:</strong> USERS must ACCEPT our POLICIES to use ICON. Otherwise, you will be redirected to the homepage.
        </p>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Checkbox checked={acceptChecked} onChange={handleAcceptChange}>
            Accept
          </Checkbox>
        </div>
        <div>
          <Checkbox checked={rejectChecked} onChange={handleRejectChange}>
            Reject
          </Checkbox>
        </div>
      </div>

      <div style={{ textAlign: 'right', marginTop: '24px' }}>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={(!acceptChecked && !rejectChecked) || submitting}
          loading={submitting}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
};

PolicyAgreementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDisapprove: PropTypes.func.isRequired,
  position: PropTypes.oneOf(['center', 'bottom-left']),
};

PolicyAgreementModal.defaultProps = {
  position: 'center',
};

export default PolicyAgreementModal;




// WEBPACK FOOTER //
// ./src/components/PolicyAgreementModal/PolicyAgreementModal.jsx